import React, { useState, useEffect } from 'react';
import UsersTab from './UsersTab';
import RolesTab from './RolesTab';
import {
  Box,
  Tabs,
  Tab, Typography, Paper, TextField, Button, Grid, Switch,
  FormControlLabel, Divider, Alert, Snackbar, CircularProgress,
  Card, CardContent, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Stack, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  ToggleOn as ToggleOnIcon
} from '@mui/icons-material';
import axios from 'axios';

interface Setting {
  id: number;
  setting_key: string;
  setting_value: string;
  setting_type: 'string' | 'number' | 'boolean' | 'json';
  description: string;
  category?: string;
  created_at?: string;
  updated_at?: string;
}

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState<Setting | null>(null);
  const [formData, setFormData] = useState({
    setting_key: '',
    setting_value: '',
    setting_type: 'string' as 'string' | 'number' | 'boolean' | 'json',
    description: '',
    category: 'Общие'
  });
  const [categories] = useState<string[]>(['Общие', 'Почта', 'Безопасность', 'Интеграции', 'Интерфейс']);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('http://192.168.50.43:5002/api/settings');

      let settingsData = response.data;
      let settingsArray: Setting[] = [];

      if (Array.isArray(settingsData)) {
        settingsArray = settingsData;
      } else if (settingsData.data && Array.isArray(settingsData.data)) {
        settingsArray = settingsData.data;
      } else if (settingsData.settings && Array.isArray(settingsData.settings)) {
        settingsArray = settingsData.settings;
      } else if (typeof settingsData === 'object') {
        settingsArray = Object.keys(settingsData).map(key => ({
          id: 0,
          setting_key: key,
          setting_value: String(settingsData[key]),
          setting_type: typeof settingsData[key] as 'string' | 'number' | 'boolean' | 'json',
          description: '',
          category: 'Общие'
        }));
      }

      // Принудительно распределяем по категориям, если их нет в БД
      settingsArray = settingsArray.map(s => {
        if (s.setting_key === 'max_bot_token') return { ...s, category: 'Интеграции' };
        return s;
      });
      setSettings(settingsArray);
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки настроек', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleOpenCreate = () => {
    setEditingSetting(null);
    setFormData({
      setting_key: '',
      setting_value: '',
      setting_type: 'string',
      description: '',
      category: 'Общие'
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (setting: Setting) => {
    setEditingSetting(setting);
    setFormData({
      setting_key: setting.setting_key,
      setting_value: setting.setting_value,
      setting_type: setting.setting_type,
      description: setting.description || '',
      category: setting.category || 'Общие'
    });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.setting_key || !formData.setting_value) {
      setSnackbar({ open: true, message: 'Заполните ключ и значение', severity: 'error' });
      return;
    }

    try {
      if (editingSetting) {
        await axios.put(`http://192.168.50.43:5002/api/settings/${formData.setting_key}`, {
          value: formData.setting_value,
          type: formData.setting_type,
          description: formData.description
        });
        setSnackbar({ open: true, message: 'Настройка обновлена', severity: 'success' });
      } else {
        await axios.post('http://192.168.50.43:5002/api/settings', {
          key: formData.setting_key,
          value: formData.setting_value,
          type: formData.setting_type,
          description: formData.description
        });
        setSnackbar({ open: true, message: 'Настройка создана', severity: 'success' });
      }

      setOpenDialog(false);
      loadSettings();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSnackbar({ open: true, message: 'Ошибка сохранения настройки', severity: 'error' });
    }
  };

  const handleDelete = async (key: string) => {
    if (!window.confirm(`Удалить настройку "${key}"?`)) return;

    try {
      await axios.delete(`http://192.168.50.43:5002/api/settings/${key}`);
      setSnackbar({ open: true, message: 'Настройка удалена', severity: 'success' });
      loadSettings();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      setSnackbar({ open: true, message: 'Ошибка удаления настройки', severity: 'error' });
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'number': return <NumbersIcon fontSize="small" />;
      case 'boolean': return <ToggleOnIcon fontSize="small" />;
      case 'json': return <CategoryIcon fontSize="small" />;
      default: return <TextFieldsIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'number': return 'info';
      case 'boolean': return 'success';
      case 'json': return 'warning';
      default: return 'default';
    }
  };

  // Группировка настроек с разделением подписантов
  const groupedSettings = settings.reduce((acc, setting) => {
    let category = setting.category || 'Общие';
    const k = setting.setting_key;

    // Подписант 1: signatory_1_* и старый формат signatory_* (без цифры),
    // но НЕ signatory_active и НЕ signatory_2_*
    if (k.startsWith('signatory_1_') ||
        (k.startsWith('signatory_') && !k.startsWith('signatory_2_') && k !== 'signatory_active')) {
      category = 'Подписант 1';
    } else if (k.startsWith('signatory_2_')) {
      category = 'Подписант 2';
    }

    if (!acc[category]) acc[category] = [];
    acc[category].push(setting);
    return acc;
  }, {} as Record<string, Setting[]>);

  // Порядок: Подписант 1 → Подписант 2 → Общие → остальные
  const categoryOrder: Record<string, number> = {
    'Подписант 1': 0,
    'Подписант 2': 1,
    'Общие': 2,
  };
  const sortedCategories = Object.keys(groupedSettings).sort((a, b) => {
    const oa = categoryOrder[a] ?? 3;
    const ob = categoryOrder[b] ?? 3;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Заголовок */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <SettingsIcon sx={{ fontSize: 40 }} />
          <Box>
            
            <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
              Управление конфигурацией приложения
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Вкладки */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="Общие настройки" />
          <Tab label="Пользователи" />
          <Tab label="Роли" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Box>
          {/* Кнопки */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadSettings} size="small">
              Обновить
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Добавить настройку
            </Button>
          </Box>

          {/* Список настроек */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : settings.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Настроек не найдено. Нажмите "Добавить настройку" для создания первой.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={3}>
              {sortedCategories.map((category) => {
                const items = groupedSettings[category];
                const isSignatory = category.startsWith('Подписант');
                return (
                <Paper key={category} sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {isSignatory ? <PersonIcon color={category.includes('1') ? 'primary' : 'secondary'} /> : <CategoryIcon color="primary" />}
                    <Typography variant="h6" fontWeight="bold">
                      {category}
                    </Typography>
                    <Chip label={`${items.length}`} size="small" variant="outlined" color={isSignatory ? (category.includes('1') ? 'primary' : 'secondary') : 'default'} />
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell width="20%">Ключ</TableCell>
                          <TableCell width="35%">Значение</TableCell>
                          <TableCell width="15%">Тип</TableCell>
                          <TableCell width="20%">Описание</TableCell>
                          <TableCell width="10%" align="right">Действия</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((setting) => (
                          <TableRow key={setting.id || setting.setting_key} hover>
                            <TableCell>
                              <Chip
                                label={setting.setting_key}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {setting.setting_type === 'boolean' ? (
                                <Chip
                                  label={setting.setting_value === 'true' ? 'Вкл' : 'Выкл'}
                                  size="small"
                                  color={setting.setting_value === 'true' ? 'success' : 'error'}
                                />
                              ) : setting.setting_type === 'json' ? (
                                <code style={{
                                  fontSize: '0.75rem',
                                  background: '#f5f5f5',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  wordBreak: 'break-all'
                                }}>
                                  {setting.setting_value.substring(0, 50)}
                                  {setting.setting_value.length > 50 ? '...' : ''}
                                </code>
                              ) : (
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                  {setting.setting_value}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                icon={getTypeIcon(setting.setting_type)}
                                label={setting.setting_type}
                                size="small"
                                color={getTypeColor(setting.setting_type) as any}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>
                                {setting.description || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <Tooltip title="Редактировать">
                                <IconButton size="small" color="primary" onClick={() => handleOpenEdit(setting)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Удалить">
                                <IconButton size="small" color="error" onClick={() => handleDelete(setting.setting_key)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )})}
            </Stack>
          )}
        </Box>
      )}

      {tabValue === 1 && (
        <UsersTab />
      )}

      {tabValue === 2 && (
        <RolesTab />
      )}

      {/* Диалог */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          p: 2
        }}>
          <Typography variant="h6">
            {editingSetting ? 'Редактирование настройки' : 'Новая настройка'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Ключ настройки"
              value={formData.setting_key}
              onChange={(e) => setFormData({ ...formData, setting_key: e.target.value })}
              fullWidth
              required
              disabled={!!editingSetting}
              helperText={editingSetting ? 'Ключ нельзя изменить' : 'Уникальный идентификатор настройки'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SettingsIcon fontSize="small" color="primary" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />

            <TextField
              label="Значение"
              value={formData.setting_value}
              onChange={(e) => setFormData({ ...formData, setting_value: e.target.value })}
              fullWidth
              required
              multiline={formData.setting_type === 'json'}
              rows={formData.setting_type === 'json' ? 4 : 1}
              helperText={formData.setting_type === 'json' ? 'Введите валидный JSON' : ''}
              size="small"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Тип данных</InputLabel>
              <Select
                value={formData.setting_type}
                onChange={(e) => setFormData({ ...formData, setting_type: e.target.value as any })}
                label="Тип данных"
              >
                <MenuItem value="string">Строка</MenuItem>
                <MenuItem value="number">Число</MenuItem>
                <MenuItem value="boolean">Булево (true/false)</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Описание"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Что делает эта настройка"
              size="small"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Категория</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Категория"
              >
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setOpenDialog(false)} variant="outlined">
            Отмена
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            {editingSetting ? 'Обновить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ bottom: 24, left: 24, maxWidth: 400 }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 1, width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
